import { Target } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  GOAL_FREQUENCY_LABEL,
  type PartnerGoal,
  type PartnerGoalTarget,
} from "@colab/shared";

type GoalsWidgetProps = {
  goals: Array<{ goal: PartnerGoal; target: PartnerGoalTarget | null }>;
};

// Partner-facing Goals widget for the dashboard. Each goal pairs with its
// "active-on-today" target; if no period is currently active the most recent
// target is shown. Goals without any target render as "not yet scheduled".
// Empty state (zero goals) hides the widget — the dashboard shouldn't show a
// Goals card to partners who don't have any assigned yet.
export function GoalsWidget({ goals }: GoalsWidgetProps) {
  if (goals.length === 0) return null;

  return (
    <section className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-accent" />
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Your goals
          </h2>
        </div>
        <Badge variant="secondary" className="text-[10px]">
          {goals.length} active
        </Badge>
      </div>

      <ul className="mt-4 space-y-4">
        {goals.map(({ goal, target }) => (
          <GoalRow key={goal.id} goal={goal} target={target} />
        ))}
      </ul>
    </section>
  );
}

function GoalRow({ goal, target }: { goal: PartnerGoal; target: PartnerGoalTarget | null }) {
  if (target === null) {
    return (
      <li className="rounded-lg border border-dashed bg-muted/20 p-4">
        <p className="text-sm font-medium text-foreground">{goal.title}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {GOAL_FREQUENCY_LABEL[goal.frequency]} goal — no period scheduled yet.
        </p>
      </li>
    );
  }

  const pct =
    target.target_value > 0
      ? Math.min(100, Math.max(0, (target.current_value / target.target_value) * 100))
      : 0;

  return (
    <li className="rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{goal.title}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {GOAL_FREQUENCY_LABEL[goal.frequency]} · ends {formatDate(target.period_end)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold tabular-nums">
            {formatNumber(target.current_value)} / {formatNumber(target.target_value)}
          </p>
          <p className="text-[11px] text-muted-foreground">{Math.round(pct)}%</p>
        </div>
      </div>
      <Progress value={pct} className="mt-3 h-1.5" />
    </li>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}
